import { useTranslation } from "react-i18next";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { SectionCard } from "./SectionCard";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCompactNumber, formatNumber } from "../utils";
import { useTokensOverTime, type StatsFilters } from "../api";

type TokenSeriesKey = "prompt_tokens" | "completion_tokens" | "total_tokens";

type Props = { filters: StatsFilters };

const TOKEN_SERIES: TokenSeriesKey[] = [
  "prompt_tokens",
  "completion_tokens",
  "total_tokens",
];

const SERIES_COLORS: Record<TokenSeriesKey, string> = {
  prompt_tokens: "#8b5cf6",
  completion_tokens: "#f87171",
  total_tokens: "#14b8a6",
};

const SERIES_GRADIENTS: Record<TokenSeriesKey, string> = {
  prompt_tokens: "colorPrompt",
  completion_tokens: "colorCompletion",
  total_tokens: "colorTotal",
};

export function TotalsOverTime({ filters }: Props) {
  const { t } = useTranslation();
  const tokens = useTokensOverTime(filters);

  const loading = tokens.isLoading;
  const data = tokens.data ?? [];

  const labelFor = (key: TokenSeriesKey) => {
    const labels: Record<TokenSeriesKey, string> = {
      prompt_tokens: t("usageAndCost.totalsOverTime.promptTokens"),
      completion_tokens: t("usageAndCost.totalsOverTime.completionTokens"),
      total_tokens: t("usageAndCost.totalsOverTime.totalTokensSeries"),
    };

    return labels[key];
  };

  return (
    <SectionCard title={t("usageAndCost.totalsOverTime.title")}>
      <div className="min-h-[280px] w-full flex-1" dir="ltr">
        {loading ? (
          <Skeleton className="h-full min-h-[280px] w-full" />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={data}
              margin={{ top: 5, right: 16, bottom: 0, left: 0 }}
            >
              <defs>
                <linearGradient id="colorPrompt" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0} />
                </linearGradient>
                <linearGradient
                  id="colorCompletion"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor="#f87171" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#f87171" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#14b8a6" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#14b8a6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--border)"
                opacity={0.4}
                vertical={false}
              />
              <XAxis
                dataKey="date"
                tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => formatCompactNumber(v as number)}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "var(--popover)",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  fontSize: 12,
                  color: "var(--popover-foreground)",
                }}
                formatter={(value: number) => formatNumber(value)}
              />
              <Legend
                iconType="plainline"
                iconSize={18}
                verticalAlign="top"
                align="center"
                height={28}
                wrapperStyle={{
                  fontSize: 12,
                  color: "var(--muted-foreground)",
                  paddingBottom: 8,
                }}
              />
              {TOKEN_SERIES.map((key) => (
                <Area
                  key={key}
                  type="monotone"
                  dataKey={key}
                  name={labelFor(key)}
                  stroke={SERIES_COLORS[key]}
                  fill={`url(#${SERIES_GRADIENTS[key]})`}
                  strokeWidth={2}
                  dot={{ r: 3, fill: SERIES_COLORS[key], strokeWidth: 0 }}
                  activeDot={{ r: 4 }}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </SectionCard>
  );
}
